<script setup lang="ts">
import { Lock } from 'lucide-vue-next'
import Mockup from './Mockup.vue'

import type { LoyaltyReward, LoyaltyTexts, MerchantColors } from '@/types/types'

const { t } = useI18n()

defineProps<{
  rewards: LoyaltyReward[]
  rewardColors: MerchantColors
  rewardTexts: LoyaltyTexts
}>()

const points = ref(10)
</script>
<template>
  <Mockup>
    <div class="-z-10 flex h-full w-full flex-col rounded-xl">
      <div class="flex items-center justify-between rounded-t-xl bg-white p-2">
        <div class="text-left">
          <h3 class="text-2xl font-bold">{{ rewardTexts?.rewardsTitle }}</h3>
          <p class="text-xs">{{ rewardTexts?.rewardsSubtitle }}</p>
        </div>
        <div
          class="rounded-full border px-3 py-1 uppercase tracking-wide"
          :style="`border-color: ${rewardColors?.button}`"
        >
          <p class="text-[0.6rem] font-bold" :style="`color: ${rewardColors?.button}`">
            {{ t('global.words.logout') }}
          </p>
        </div>
      </div>
      <div class="flex h-full flex-col rounded-b-xl p-2" :style="`background: ${rewardColors?.background}`">
        <div class="my-5 text-center text-white" :class="{ 'my-14': rewards.length === 0 }">
          <p class="font-light uppercase">{{ rewardTexts?.rewardsText }}</p>
          <p class="text-4xl font-extrabold">
            {{ t('pages.loyaltyPage.grid.points', { points: points }, points) }}
          </p>
        </div>
        <div class="grid-template grid h-full w-full grid-flow-col justify-items-center gap-2 overflow-x-auto">
          <div
            v-for="reward in rewards"
            :key="reward.id"
            class="flex h-max w-full flex-col items-center justify-between rounded-sm bg-white p-1"
          >
            <div class="relative">
              <img class="z-10 rounded" :src="reward.pictureUrl" alt="" />
              <div
                v-if="reward.points > points"
                class="absolute top-0 z-50 flex h-full w-full flex-col items-center justify-center gap-4 rounded-sm text-center"
                :style="`background: ${rewardColors?.background}80`"
              >
                <div class="rounded-full bg-white p-2">
                  <Lock class="h-5 w-5" :style="`color: ${rewardColors?.background}`" />
                </div>
                <div class="w-7/12 break-words text-xs font-bold uppercase tracking-wide text-white">
                  <p>
                    <span>{{ t('pages.loyaltyPage.mockup.missingPoints.text') }}</span>
                    {{
                      t('pages.loyaltyPage.mockup.missingPoints.points', {
                        missingPoints: reward.points - points
                      })
                    }}
                  </p>
                </div>
              </div>
            </div>
            <div class="flex h-full flex-col items-center justify-center p-2 text-sm">
              <h3 class="line-clamp-2 font-bold">{{ reward.name }}</h3>
              <p class="font-light">
                {{ t('pages.loyaltyPage.grid.points', { points: reward.points }, reward.points) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Mockup>
</template>
<style scoped>
.grid-template {
  grid-auto-columns: 30%;
}

@media screen and (max-width: 768px) {
  .grid-template {
    grid-template-columns: repeat(2, 40%);
    grid-template-rows: repeat(1, 40%);
    grid-auto-columns: 40%;
  }
}
</style>
